import { openDB, deleteDB } from 'idb'
import type { DBSchema, IDBPDatabase } from 'idb'
import type {
  MyTurfAssignmentDto,
  ResolvedScriptDto,
  RecordVisitRequest,
  RouteOutcomeRequest,
} from '../types/canvasser'
import type { CallOutcome, FollowUpCallBatchDetailDto, FollowUpStatus, RecordCallSubmission } from '../types/followUp'

// ─── Database Schema ─────────────────────────────────────────────

interface CanvasserDB extends DBSchema {
  walkListCache: {
    key: string
    value: {
      campaignId: string
      turfs: MyTurfAssignmentDto[]
      cachedAt: number
    }
  }
  scriptCache: {
    key: string
    value: {
      key: string
      script: ResolvedScriptDto
      cachedAt: number
    }
  }
  pendingVisits: {
    key: number
    value: PendingVisit
  }
  localVisitStatus: {
    key: string
    value: {
      contactId: string
      isVisited: boolean
      hasComeBackLater?: boolean
      /** [CF-1 P5] turfId → that knock's come-back-later flag. Absent on a pre-P5 record. */
      turfs?: Record<string, boolean>
      updatedAt: number
    }
  }
  doneHereMarkers: {
    key: string
    value: { key: string; turfId: string; stopKey: string; localDate: string; markedAt: number }
  }
  placementFlips: {
    key: string
    value: PlacementFlip
  }
  routeOutcomes: {
    key: string
    value: QueuedRouteOutcome
  }
  callBatchCache: {
    key: string
    value: { batchId: string; batch: FollowUpCallBatchDetailDto; cachedAt: number }
  }
  pendingCalls: {
    key: number
    value: PendingCall
  }
}

export interface PendingVisit {
  visit: RecordVisitRequest
  retryCount: number
  queuedAt: number
  lastError?: string
  /**
   * [C2 finding 10] TERMINAL: the server's scope gate rejected this visit (turf reassigned
   * mid-shift, campaign closed, older than the late-sync window). Never auto-retried and never
   * silently dropped — it is the canvasser's own work — and surfaced in the sync banner so they
   * can show their organizer. The [LEGAL] TTL sweep already spares this store.
   */
  rejected?: boolean
  rejectedReason?: string
}

// ─── Database Access ─────────────────────────────────────────────

const DB_NAME = 'canvasser-offline'
// v5 (CF-1 P4): + callBatchCache, pendingCalls. Additive; clears nothing.
// v4 (CF-1 P3): + routeOutcomes. Additive; clears nothing.
// v3 (Phase 4): + placementFlips. Additive; clears nothing (same reasoning as v2).
// v2 (Phase 2): + doneHereMarkers. Deliberately does NOT clear walkListCache/scriptCache — new DTO fields are optional+defaulted, and clearing would strand an offline canvasser mid-update (plan Global Constraints).
const DB_VERSION = 5

let dbPromise: Promise<IDBPDatabase<CanvasserDB>> | null = null

function getDB(): Promise<IDBPDatabase<CanvasserDB>> {
  if (!dbPromise) {
    const opening: Promise<IDBPDatabase<CanvasserDB>> = openDB<CanvasserDB>(DB_NAME, DB_VERSION, {
      // A newer app version (another tab, or this tab after an update) wants to upgrade: close THIS
      // connection so it never blocks that upgrade, and drop the singleton so the next access
      // reopens at whatever version now exists.
      blocking(_current, _blocked, event) {
        ;(event.target as IDBDatabase).close()
        if (dbPromise === opening) dbPromise = null
      },
      // An older connection elsewhere hasn't closed yet. Nothing to do here: the open request stays
      // pending and completes once that connection closes (its own `blocking` handler does that).
      blocked() { /* wait */ },
      upgrade(db) {
        if (!db.objectStoreNames.contains('walkListCache')) {
          db.createObjectStore('walkListCache', { keyPath: 'campaignId' })
        }
        if (!db.objectStoreNames.contains('scriptCache')) {
          db.createObjectStore('scriptCache', { keyPath: 'key' })
        }
        if (!db.objectStoreNames.contains('pendingVisits')) {
          db.createObjectStore('pendingVisits', { autoIncrement: true })
        }
        if (!db.objectStoreNames.contains('localVisitStatus')) {
          db.createObjectStore('localVisitStatus', { keyPath: 'contactId' })
        }
        if (!db.objectStoreNames.contains('doneHereMarkers')) {
          db.createObjectStore('doneHereMarkers', { keyPath: 'key' })
        }
        if (!db.objectStoreNames.contains('placementFlips')) {
          db.createObjectStore('placementFlips', { keyPath: 'turfMemberId' })
        }
        if (!db.objectStoreNames.contains('routeOutcomes')) {
          db.createObjectStore('routeOutcomes', { keyPath: 'clientOutcomeId' })
        }
        if (!db.objectStoreNames.contains('callBatchCache')) {
          db.createObjectStore('callBatchCache', { keyPath: 'batchId' })
        }
        if (!db.objectStoreNames.contains('pendingCalls')) {
          db.createObjectStore('pendingCalls', { autoIncrement: true })
        }
      },
    })
    dbPromise = opening
  }
  return dbPromise
}

// ─── Cache TTL ([LEGAL] retention — legal research §6a) ──────────

/** Delivered walk-list / script PII self-expires 7 days after its last successful sync. */
export const WALK_LIST_TTL_MS = 7 * 24 * 60 * 60 * 1000

export type CacheRead<T> =
  | { status: 'hit'; data: T }
  | { status: 'expired' }
  | { status: 'miss' }

/** Thrown by the offline-fallback hooks when the only cached copy has passed its TTL. */
export class CacheExpiredError extends Error {
  constructor() {
    super('Offline cache expired')
    this.name = 'CacheExpiredError'
  }
}

export function isCacheExpiredError(err: unknown): err is CacheExpiredError {
  return err instanceof CacheExpiredError
}

function isExpired(cachedAt: number, now: number): boolean {
  return now - cachedAt > WALK_LIST_TTL_MS
}

// ─── Walk List Cache ─────────────────────────────────────────────

export async function cacheWalkList(
  campaignId: string,
  turfs: MyTurfAssignmentDto[]
): Promise<void> {
  const db = await getDB()
  await db.put('walkListCache', { campaignId, turfs, cachedAt: Date.now() })
}

export async function getCachedWalkList(
  campaignId: string
): Promise<CacheRead<MyTurfAssignmentDto[]>> {
  const db = await getDB()
  const entry = await db.get('walkListCache', campaignId)
  if (!entry) return { status: 'miss' }
  if (isExpired(entry.cachedAt, Date.now())) {
    await db.delete('walkListCache', campaignId) // [LEGAL] purge stale PII on read
    return { status: 'expired' }
  }
  return { status: 'hit', data: entry.turfs }
}

// ─── Script Cache ────────────────────────────────────────────────

function scriptKey(campaignId: string, contactId: string, turfId: string) {
  return `${campaignId}:${contactId}:${turfId}`
}

export async function cacheScript(
  campaignId: string,
  contactId: string,
  turfId: string,
  script: ResolvedScriptDto
): Promise<void> {
  const db = await getDB()
  const key = scriptKey(campaignId, contactId, turfId)
  await db.put('scriptCache', { key, script, cachedAt: Date.now() })
}

export async function getCachedScript(
  campaignId: string,
  contactId: string,
  turfId: string
): Promise<CacheRead<ResolvedScriptDto>> {
  const db = await getDB()
  const key = scriptKey(campaignId, contactId, turfId)
  const entry = await db.get('scriptCache', key)
  if (!entry) return { status: 'miss' }
  if (isExpired(entry.cachedAt, Date.now())) {
    await db.delete('scriptCache', key) // [LEGAL] purge stale PII on read
    return { status: 'expired' }
  }
  return { status: 'hit', data: entry.script }
}

// ─── Mount-time sweep ([LEGAL] — purge stale delivered PII on app open) ──

/**
 * Deletes every walk-list / script cache entry older than the TTL. Does NOT touch
 * `pendingVisits` / `localVisitStatus` — those are the canvasser's own outbound work,
 * governed by sync/revoke, not the delivered-list clock (spec §2.3). Called once on
 * the canvasser-shell mount so stale PII self-expires on next app open.
 */
export async function sweepExpiredCache(): Promise<{ walkListsPurged: number; scriptsPurged: number; callBatchesPurged: number }> {
  const db = await getDB()
  const now = Date.now()
  let walkListsPurged = 0
  let scriptsPurged = 0
  let callBatchesPurged = 0

  for (const entry of await db.getAll('walkListCache')) {
    if (isExpired(entry.cachedAt, now)) {
      await db.delete('walkListCache', entry.campaignId)
      walkListsPurged++
    }
  }
  for (const entry of await db.getAll('scriptCache')) {
    if (isExpired(entry.cachedAt, now)) {
      await db.delete('scriptCache', entry.key)
      scriptsPurged++
    }
  }
  // [CF-1 P4] A delivered call list is the same PII on the same clock. pendingCalls is outbound work: untouched.
  for (const entry of await db.getAll('callBatchCache')) {
    if (isExpired(entry.cachedAt, now)) {
      await db.delete('callBatchCache', entry.batchId)
      callBatchesPurged++
    }
  }
  return { walkListsPurged, scriptsPurged, callBatchesPurged }
}

// ─── Pending Visits Queue ────────────────────────────────────────

export async function queueVisit(visit: RecordVisitRequest): Promise<number> {
  const db = await getDB()
  return db.add('pendingVisits', {
    visit,
    retryCount: 0,
    queuedAt: Date.now(),
  })
}

export async function getPendingVisits(): Promise<
  Array<{ id: number; entry: PendingVisit }>
> {
  const db = await getDB()
  const tx = db.transaction('pendingVisits', 'readonly')
  const store = tx.objectStore('pendingVisits')
  let cursor = await store.openCursor()
  const results: Array<{ id: number; entry: PendingVisit }> = []
  while (cursor) {
    results.push({ id: cursor.key, entry: cursor.value })
    cursor = await cursor.continue()
  }
  return results
}

export async function getPendingVisitCount(): Promise<number> {
  const db = await getDB()
  return db.count('pendingVisits')
}

export async function getRejectedVisits(): Promise<
  Array<{ id: number; entry: PendingVisit }>
> {
  const all = await getPendingVisits()
  return all.filter(r => r.entry.rejected === true)
}

export async function removePendingVisit(id: number): Promise<void> {
  const db = await getDB()
  await db.delete('pendingVisits', id)
}

export async function updatePendingVisit(
  id: number,
  updates: Partial<PendingVisit>
): Promise<void> {
  const db = await getDB()
  const existing = await db.get('pendingVisits', id)
  if (existing) {
    await db.put('pendingVisits', { ...existing, ...updates }, id)
  }
}

// ─── Local Visit Status ──────────────────────────────────────────

export interface LocalVisitState {
  isVisited: boolean
  hasComeBackLater: boolean
  /** [CF-1 P5] Which turfs this person was knocked on (turfId → come-back-later), so a chase-route
   *  stop only reads its own knock. Absent on a pre-P5 record (treated as a canvass knock). */
  turfs?: Record<string, boolean>
}

/**
 * Records a local knock. The store stays keyed by contactId (DB version unchanged); [CF-1 P5] the
 * turf is merged into the record's `turfs` map so a canvass knock and a chase knock on the same
 * person both survive until sync.
 */
export async function setLocalVisitStatus(
  contactId: string,
  hasComeBackLater = false,
  turfId?: string,
): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('localVisitStatus', 'readwrite')
  const existing = await tx.store.get(contactId)
  const turfs = turfId ? { ...(existing?.turfs ?? {}), [turfId]: hasComeBackLater } : existing?.turfs
  await tx.store.put({
    contactId,
    isVisited: true,
    hasComeBackLater,
    ...(turfs ? { turfs } : {}),
    updatedAt: Date.now(),
  })
  await tx.done
}

export async function getLocalVisitStatuses(): Promise<Map<string, LocalVisitState>> {
  const db = await getDB()
  const all = await db.getAll('localVisitStatus')
  const map = new Map<string, LocalVisitState>()
  for (const entry of all) {
    map.set(entry.contactId, {
      isVisited: entry.isVisited,
      hasComeBackLater: entry.hasComeBackLater ?? false,
      ...(entry.turfs ? { turfs: entry.turfs } : {}),
    })
  }
  return map
}

export async function clearLocalVisitStatuses(): Promise<void> {
  const db = await getDB()
  await db.clear('localVisitStatus')
}

// ─── Full Wipe (logout / revoke / expiry) ────────────────────────

/**
 * [LEGAL] Blunt wipe of all cached canvasser PII (walk lists, scripts, pending visits, local
 * status). Called on logout in 1d-1; 1d-2 extends the callers to revoke/expiry/TTL.
 */
export async function wipeOfflineData(): Promise<void> {
  if (dbPromise) {
    // deleteDB() blocks until every open connection is closed — dropping the JS reference
    // alone does not close it, so without this the promise below can hang indefinitely on
    // a persistent SPA/Capacitor shell that already opened the DB this session.
    try {
      ;(await dbPromise).close()
    } catch {
      /* already closing/closed */
    }
  }
  dbPromise = null // reset the singleton so a later access reopens a fresh DB
  wipeDoorSessionState()
  await deleteDB(DB_NAME)
}

/** [Phase 4 R10] Door drafts and per-door script state hold PII in sessionStorage; sign-out clears them too. */
function wipeDoorSessionState(): void {
  try {
    const doomed: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      // 'call:' = a call's saved conversation path (CF-1 P4, `call:{batchId}:{itemId}`).
      if (k && (k.startsWith('canvasser_door_') || k.startsWith('canvasser_script_') || k.startsWith('call:'))) doomed.push(k)
    }
    doomed.forEach(k => sessionStorage.removeItem(k))
  } catch {
    /* sessionStorage unavailable */
  }
}

// ─── Done-here markers (spec §15.1 S4) ───────────────────────────

/** Local calendar date, YYYY-MM-DD. "Today" for a canvasser is their local day, not UTC. */
export function localDateKey(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Worksite stop finished for today ("Done here", or any outcome recorded there — Phase 4 writes those). */
export async function markStopDoneHere(turfId: string, stopKey: string, localDate = localDateKey()): Promise<void> {
  const db = await getDB()
  await db.put('doneHereMarkers', {
    key: `${turfId}|${stopKey}|${localDate}`, turfId, stopKey, localDate, markedAt: Date.now(),
  })
}

/** Today's done-here stop keys for a turf. Purges markers from other days as it reads. */
export async function getStopsDoneHere(turfId: string, localDate = localDateKey()): Promise<Set<string>> {
  const db = await getDB()
  const tx = db.transaction('doneHereMarkers', 'readwrite')
  const result = new Set<string>()
  for (const m of await tx.store.getAll()) {
    if (m.localDate !== localDate) await tx.store.delete(m.key)
    else if (m.turfId === turfId) result.add(m.stopKey)
  }
  await tx.done
  return result
}

/** Today's done-here stop keys for EVERY turf, keyed by turfId. Purges other days' markers as it reads. */
export async function getAllStopsDoneHere(localDate = localDateKey()): Promise<Map<string, Set<string>>> {
  const db = await getDB()
  const tx = db.transaction('doneHereMarkers', 'readwrite')
  const result = new Map<string, Set<string>>()
  for (const m of await tx.store.getAll()) {
    if (m.localDate !== localDate) { await tx.store.delete(m.key); continue }
    const set = result.get(m.turfId)
    if (set) set.add(m.stopKey)
    else result.set(m.turfId, new Set([m.stopKey]))
  }
  await tx.done
  return result
}

// ─── Placement flips ("Visit at home today", spec §15.1 D4 / P4-S3) ─────────

export interface PlacementFlip {
  turfMemberId: string
  turfId: string
  contactId: string
  /** The NotAtWork visit this flip follows: never sent before that visit has synced (its WorksiteId
   *  stamp depends on the member still being placed at work when it records). */
  afterClientVisitId: string
  movedFromLabel: string
  localDate: string
  synced: boolean
  retryCount: number
}

export async function savePlacementFlip(f: PlacementFlip): Promise<void> {
  const db = await getDB()
  await db.put('placementFlips', f)
}

/**
 * Today's flips (synced or not); other days' are purged as they are read. A purged UNSYNCED flip is
 * lost on purpose: its visit still syncs, and the organizer can place the person home in O1's review.
 */
export async function getPlacementFlips(localDate: string = localDateKey()): Promise<PlacementFlip[]> {
  const db = await getDB()
  const all = await db.getAll('placementFlips')
  for (const f of all) if (f.localDate !== localDate) await db.delete('placementFlips', f.turfMemberId)
  return all.filter(f => f.localDate === localDate)
}

export async function updatePlacementFlip(turfMemberId: string, patch: Partial<PlacementFlip>): Promise<void> {
  const db = await getDB()
  const cur = await db.get('placementFlips', turfMemberId)
  if (cur) await db.put('placementFlips', { ...cur, ...patch, turfMemberId })
}

export async function removePlacementFlip(turfMemberId: string): Promise<void> {
  const db = await getDB()
  await db.delete('placementFlips', turfMemberId)
}

// ─── Route outcomes (CF-1 P3: action-route "Dropped off" / "Couldn't") ───────

export interface QueuedRouteOutcome {
  clientOutcomeId: string
  outcome: RouteOutcomeRequest
  localDate: string
  synced: boolean
  retryCount: number
  /**
   * [R5] The grant session that recorded it (from the grant mirror). Outcomes sync under whichever
   * grant_session cookie is current, so only the current grant's are sent. Additive field, no DB bump:
   * legacy entries without it drain under the current grant, as before.
   */
  grantId?: string
  /** TERMINAL: the server refused it. Kept (the canvasser's own work), never retried, surfaced. */
  rejected?: boolean
  rejectedReason?: string
}

export async function saveRouteOutcomes(list: QueuedRouteOutcome[]): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('routeOutcomes', 'readwrite')
  for (const o of list) await tx.store.put(o)
  await tx.done
}

/**
 * Today's outcomes (synced or not). Other days' SYNCED entries are purged as they are read; unsynced
 * and rejected entries are never purged, whatever their day: they are the canvasser's own work.
 */
export async function getRouteOutcomes(localDate: string = localDateKey()): Promise<QueuedRouteOutcome[]> {
  const db = await getDB()
  const all = await db.getAll('routeOutcomes')
  const kept: QueuedRouteOutcome[] = []
  for (const o of all) {
    if (o.localDate !== localDate && o.synced && !o.rejected) await db.delete('routeOutcomes', o.clientOutcomeId)
    else kept.push(o)
  }
  return kept
}

export async function updateRouteOutcome(clientOutcomeId: string, patch: Partial<QueuedRouteOutcome>): Promise<void> {
  const db = await getDB()
  const cur = await db.get('routeOutcomes', clientOutcomeId)
  if (cur) await db.put('routeOutcomes', { ...cur, ...patch, clientOutcomeId })
}

// ─── Call batches (CF-1 P4: the canvasser's follow-up call list) ─────────────

export async function cacheCallBatch(batch: FollowUpCallBatchDetailDto): Promise<void> {
  const db = await getDB()
  await db.put('callBatchCache', { batchId: batch.batch.id, batch, cachedAt: Date.now() })
}

export async function getCachedCallBatch(batchId: string): Promise<CacheRead<FollowUpCallBatchDetailDto>> {
  const db = await getDB()
  const entry = await db.get('callBatchCache', batchId)
  if (!entry) return { status: 'miss' }
  if (isExpired(entry.cachedAt, Date.now())) {
    await db.delete('callBatchCache', batchId) // [LEGAL] purge stale PII on read
    return { status: 'expired' }
  }
  return { status: 'hit', data: entry.batch }
}

/**
 * After a call syncs, write it onto the cached batch row (cachedAt untouched: no TTL extension), so a
 * later fallback read of this copy (failed refetch, cold start offline) shows the call instead of a
 * markable pre-drain row. `itemStatus` is the server's; null leaves the status as cached.
 */
export async function markCachedCallRow(
  batchId: string, itemId: string,
  call: { outcome: CallOutcome; itemStatus: FollowUpStatus | null; markDoNotContact?: boolean },
): Promise<void> {
  const db = await getDB()
  const entry = await db.get('callBatchCache', batchId)
  if (!entry) return
  const items = entry.batch.items.map(r => r.itemId !== itemId ? r : {
    ...r,
    calledAt: new Date().toISOString(),
    lastOutcome: call.outcome,
    attemptCount: r.attemptCount + 1,
    status: call.itemStatus ?? r.status,
    doNotContact: r.doNotContact || !!call.markDoNotContact,
  })
  await db.put('callBatchCache', { ...entry, batch: { ...entry.batch, items } })
}

/** A call's script lives in scriptCache under its own key family, so the TTL sweep covers it. */
function callScriptKey(batchId: string, itemId: string) {
  return `call:${batchId}:${itemId}`
}

export async function cacheCallScript(batchId: string, itemId: string, script: ResolvedScriptDto): Promise<void> {
  const db = await getDB()
  await db.put('scriptCache', { key: callScriptKey(batchId, itemId), script, cachedAt: Date.now() })
}

export async function getCachedCallScript(batchId: string, itemId: string): Promise<CacheRead<ResolvedScriptDto>> {
  const db = await getDB()
  const key = callScriptKey(batchId, itemId)
  const entry = await db.get('scriptCache', key)
  if (!entry) return { status: 'miss' }
  if (isExpired(entry.cachedAt, Date.now())) {
    await db.delete('scriptCache', key) // [LEGAL] purge stale PII on read
    return { status: 'expired' }
  }
  return { status: 'hit', data: entry.script }
}

export interface PendingCall {
  call: RecordCallSubmission
  batchId: string
  /**
   * [R4] The grant session that recorded it (from the grant mirror). Calls sync under whichever
   * grant_session cookie is current, so only the current grant's are sent; another grant's stay queued
   * (never wiped) and block a "Canvass this" switch until they drain.
   */
  grantId?: string
  retryCount: number
  queuedAt: number
  lastError?: string
  /** TERMINAL: the server refused it (e.g. the list was reassigned). Kept, never retried, surfaced. */
  rejected?: boolean
  rejectedReason?: string
}

export async function queueCall(
  call: RecordCallSubmission, ctx: { batchId: string; grantId?: string },
): Promise<number> {
  const db = await getDB()
  return db.add('pendingCalls', { call, batchId: ctx.batchId, grantId: ctx.grantId, retryCount: 0, queuedAt: Date.now() })
}

export async function getPendingCalls(): Promise<Array<{ id: number; entry: PendingCall }>> {
  const db = await getDB()
  const tx = db.transaction('pendingCalls', 'readonly')
  let cursor = await tx.store.openCursor()
  const results: Array<{ id: number; entry: PendingCall }> = []
  while (cursor) {
    results.push({ id: cursor.key, entry: cursor.value })
    cursor = await cursor.continue()
  }
  return results
}

export async function getPendingCallCount(): Promise<number> {
  const db = await getDB()
  return db.count('pendingCalls')
}

export async function updatePendingCall(id: number, patch: Partial<PendingCall>): Promise<void> {
  const db = await getDB()
  const existing = await db.get('pendingCalls', id)
  if (existing) await db.put('pendingCalls', { ...existing, ...patch }, id)
}

export async function removePendingCall(id: number): Promise<void> {
  const db = await getDB()
  await db.delete('pendingCalls', id)
}
